"use client"
import { useState, useEffect, useRef } from "react"
import { usePathname } from "next/navigation"
import Image from "next/image"
import Script from "next/script"
import Icon from "@/components/ui/Icon"

const TURNSTILE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY

export default function NewsletterPopup() {
  const [show, setShow] = useState(false)
  const [form, setForm] = useState({ name: "", email: "" })
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState("")
  const pathname = usePathname()
  // Admin + auth screens must never show a marketing pop-up (it also dragged the
  // Turnstile widget onto the login page, causing spurious client errors).
  const suppressed = !!pathname && (pathname.startsWith("/admin") || pathname.startsWith("/auth"))

  useEffect(() => {
    if (suppressed) return
    if (localStorage.getItem("ze_newsletter_dismissed")) return
    if (localStorage.getItem("ze_user")) return
    const timer = setTimeout(() => setShow(true), 45000)
    const onScroll = () => {
      if (window.scrollY > document.body.scrollHeight * 0.6) {
        setShow(true)
        window.removeEventListener("scroll", onScroll)
      }
    }
    window.addEventListener("scroll", onScroll)
    return () => { clearTimeout(timer); window.removeEventListener("scroll", onScroll) }
  }, [suppressed])

  const dialogRef = useRef<HTMLDivElement>(null)
  const firstFieldRef = useRef<HTMLInputElement>(null)

  // A modal that traps nothing, announces nothing and cannot be dismissed from
  // the keyboard is not usable with a screen reader or without a mouse. Escape
  // closes it, focus moves into it on open and is kept inside while it is open,
  // and it returns to wherever it came from on close.
  useEffect(() => {
    if (!show || suppressed) return
    const previous = document.activeElement as HTMLElement | null
    firstFieldRef.current?.focus()

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") { e.preventDefault(); dismiss(); return }
      if (e.key !== "Tab") return
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      if (!focusable?.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
    }

    document.addEventListener("keydown", onKeyDown)
    // The page behind a modal must not scroll under it.
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", onKeyDown)
      document.body.style.overflow = prevOverflow
      previous?.focus?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, suppressed])

  function dismiss() {
    localStorage.setItem("ze_newsletter_dismissed", "1")
    setShow(false)
  }

  async function handleSubmit() {
    if (!form.name || !form.email) return
    setSubmitting(true)
    try {
      const turnstileToken = (window as any).turnstile?.getResponse() ?? ""
      // The result was never inspected: a rejected signup (rate limit, bot check,
      // invalid address) still showed "thanks" AND wrote the permanent dismiss
      // flag, so the visitor was never asked again and was never subscribed.
      const res = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name, email: form.email, "cf-turnstile-response": turnstileToken }),
      })
      const d = await res.json().catch(() => null)
      if (d?.success) {
        setDone(true)
        localStorage.setItem("ze_newsletter_dismissed", "1")
        setTimeout(() => setShow(false), 2500)
      } else {
        setError(d?.error ?? "Something went wrong. Please try again.")
        ;(window as any).turnstile?.reset()
      }
    } catch {
      setError("Something went wrong. Please try again.")
      ;(window as any).turnstile?.reset()
    }
    setSubmitting(false)
  }

  if (suppressed || !show) return null

  return (
    <div
      // Clicking the backdrop dismisses, which is what everyone expects of a
      // marketing overlay. The check keeps clicks inside the panel from closing it.
      onClick={e => { if (e.target === e.currentTarget) dismiss() }}
      style={{ position: "fixed", inset: 0, background: "rgba(27,45,84,0.75)", zIndex: 9998, display: "grid", placeItems: "center", padding: 24 }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ze-newsletter-title"
        style={{ background: "var(--cream)", borderRadius: 20, padding: 40, maxWidth: 440, width: "100%", position: "relative", boxShadow: "0 32px 80px rgba(0,0,0,0.25)" }}
      >
        {/* An icon-only button with no text has no accessible name at all — a
            screen reader announced it as just "button". */}
        <button onClick={dismiss} aria-label="Close newsletter sign-up" style={{ position: "absolute", top: 16, right: 16, background: "none", border: "none", cursor: "pointer", color: "var(--muted)", display: "flex" }}><Icon name="X" size={20} /></button>
        {done ? (
          <div style={{ textAlign: "center", padding: "16px 0" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 16 }}>
              <Image src="/icon-full-color.svg" width={44} height={44} alt="Zim Estates" style={{ background: "var(--cream-warm)", borderRadius: 10, padding: 5, flexShrink: 0 }} />
              <span style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 600, color: "var(--navy)" }}>Zim Estates</span>
            </div>
            <div id="ze-newsletter-title" role="status" style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 700, color: "var(--navy)", marginBottom: 8 }}>You are in.</div>
            <div style={{ color: "var(--muted)", fontSize: 14 }}>We will send you the best deals first.</div>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <Image src="/icon-full-color.svg" width={44} height={44} alt="Zim Estates" style={{ background: "var(--cream-warm)", borderRadius: 10, padding: 5, flexShrink: 0 }} />
              <span style={{ fontFamily: "Fraunces, serif", fontSize: 20, fontWeight: 600, color: "var(--navy)" }}>Zim Estates</span>
            </div>
            <h2 id="ze-newsletter-title" style={{ fontFamily: "Fraunces, serif", fontSize: 26, fontWeight: 700, color: "var(--navy)", marginBottom: 8 }}>Don't miss the best deals</h2>
            <p style={{ color: "var(--muted)", fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>Join Zimbabweans getting new listings, price drops and market insights every week. Be first to know.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
              {/* Placeholder-only labelling fails WCAG 3.3.2: the label disappears
                  the moment you start typing, and some screen readers never read
                  it at all. aria-label keeps the visual design unchanged. */}
              <input ref={firstFieldRef} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} aria-label="Your first name" autoComplete="given-name" placeholder="Your first name" style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none" }} />
              <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} aria-label="Your email address" autoComplete="email" placeholder="Your email address" type="email" style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 14, fontFamily: "inherit", background: "var(--cream)", outline: "none" }} />
            </div>
            {TURNSTILE_KEY && (
              <>
                <Script src="https://challenges.cloudflare.com/turnstile/v0/api.js" strategy="lazyOnload" />
                <div className="cf-turnstile" data-sitekey={TURNSTILE_KEY} data-error-callback="zeTurnstileError" data-retry="auto" data-theme="light" style={{ marginBottom: 4 }} />
              </>
            )}
            {error && <div role="alert" style={{ fontSize: 13, color: "var(--red-text)", padding: "8px 12px", background: "rgba(196,68,68,0.06)", borderRadius: 8, marginBottom: 10 }}>{error}</div>}
            <button onClick={handleSubmit} disabled={submitting} style={{ width: "100%", padding: 14, background: submitting ? "var(--muted)" : "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: submitting ? "not-allowed" : "pointer", fontFamily: "inherit", marginBottom: 12 }}>
              {submitting ? "Joining..." : "Get early access"}
            </button>
            <div style={{ textAlign: "center", fontSize: 11, color: "var(--muted)" }}>No spam. Unsubscribe anytime.</div>
          </>
        )}
      </div>
    </div>
  )
}